/**
 * Jerarquía de las áreas por FRECUENCIA DE USO, no por completitud.
 *
 * El problema de producto del sistema no es que le falten pantallas: es que las
 * doce áreas pesan lo mismo. La barra lateral las listaba en orden de `sequence`
 * —un número que sólo dice quién depende de quién al arrancar— así que «Ventas»
 * (todos los días) y «Configuración» (una vez por mes, si eso) se veían igual.
 *
 * Acá se clasifican las áreas raíz en tres pisos:
 *
 *  - `diario`: lo que se abre para trabajar hoy —facturar, cobrar, atender,
 *    liquidar—. Va arriba y con más peso visual.
 *  - `gestion`: lo mensual o de respaldo —contabilidad, depósito, sueldos,
 *    fiscal—. Segundo nivel.
 *  - `config`: maestros y parámetros. Al fondo y en gris: se toca poco y no
 *    tiene que competir con el trabajo.
 *
 * La clasificación es por NOMBRE de menú raíz para que instalar un módulo nuevo
 * caiga en un piso razonable sin tocar nada: lo desconocido va a `gestion`, que
 * es el default honesto (ni lo asciende a diario ni lo esconde en config).
 */

import type { MenuNode } from '@/lib/types';

export type Tier = 'diario' | 'gestion' | 'config';

export const TIER_LABEL: Record<Tier, string> = {
  diario: 'Todos los días',
  gestion: 'Gestión',
  config: 'Configuración',
};

/** Piso explícito por nombre de menú raíz. Lo que no figure cae en `gestion`. */
const TIER_POR_MENU: Record<string, Tier> = {
  // -- Todos los días -----------------------------------------------------
  // La CARTERA va arriba de todo: es la pantalla por la que se entra todos los
  // días —administraciones, administradores, consorcios— y de ella cuelga el
  // resto del vertical. Sin esta línea caía en `gestion`, que es el default
  // honesto para lo desconocido pero acá dejaba la sección principal del sistema
  // debajo de Contabilidad y Depósito.
  'property.menu_cartera': 'diario', // Administraciones
  'invoicing.menu_root': 'diario', // Facturación
  'sales.menu_root': 'diario', // Ventas
  'property.menu_root': 'diario', // Expensas
  'crm.menu_root': 'diario', // CRM
  'base.menu_contacts': 'diario', // Contactos
  'services.menu_helpdesk': 'diario', // Mesa de ayuda
  // -- Gestión ------------------------------------------------------------
  'accounting.menu_root': 'gestion', // Contabilidad
  'inventory.menu_root': 'gestion', // Depósito
  'purchase.menu_root': 'gestion', // Compras
  'products.menu_root': 'gestion', // Catálogo
  'services.menu_projects': 'gestion', // Proyectos
  'hr.menu_root': 'gestion', // Recursos Humanos
  'l10n_ar.menu_root': 'gestion', // Fiscal (AR)
  // -- Configuración ------------------------------------------------------
  'base.menu_settings': 'config',
};

/** El piso de un menú raíz. Config también atrapa cualquier `*.menu_config`. */
export function tierOf(node: MenuNode): Tier {
  const explicito = TIER_POR_MENU[node.name];
  if (explicito !== undefined) return explicito;
  if (node.name.endsWith('menu_settings') || node.name.endsWith('menu_config')) return 'config';
  return 'gestion';
}

const ORDEN: Tier[] = ['diario', 'gestion', 'config'];

/** Agrupa las áreas raíz por piso, respetando el orden `sequence` dentro de cada uno. */
export function groupByTier(roots: MenuNode[]): Array<{ tier: Tier; nodes: MenuNode[] }> {
  const buckets = new Map<Tier, MenuNode[]>();
  for (const tier of ORDEN) buckets.set(tier, []);
  for (const node of roots) buckets.get(tierOf(node))!.push(node);
  return ORDEN.map((tier) => ({ tier, nodes: buckets.get(tier)! })).filter((b) => b.nodes.length > 0);
}
